import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { HealthDial } from '../HealthDial';
import { Reveal } from '../Reveal';
import { heroScope, heroes, openings, type HeroVariant } from '../../data/site';
interface HeroProps {
  variant: HeroVariant;
  /** Puts KeyTurn Health at the front of the hero, with a portfolio read above the openings. */
  healthLed?: boolean;
}
export function Hero({
  variant,
  healthLed = false
}: HeroProps) {
  const hero = heroes[variant];
  const rowDialSize = healthLed ? 38 : 46;
  return <section id="top" className="w-full bg-charcoal-700" data-id="element-133" data-mp="qub3yrisjql3">
      <div className="mx-auto grid max-w-[1180px] items-center gap-14 px-5 pb-20 pt-16 sm:px-8 lg:grid-cols-[minmax(0,1.02fr)_minmax(0,0.98fr)] lg:gap-16 lg:pb-28 lg:pt-24" data-id="element-134" data-mp="vmm49vhg1ee6">
        <div data-id="element-135" data-mp="10njn8aaa2cgq">
          <Reveal onMount data-id="element-136" data-mp="15kif0m6gy7br">
            <p className="micro-label text-[9px] text-gold-400" data-id="element-137" data-mp="15lbcsxng9mhr">
              {healthLed ? 'Powered by KeyTurn Health' : 'Hotel project intelligence'}
            </p>
          </Reveal>

          <Reveal onMount delay={0.05} data-id="element-138" data-mp="9oc7ckrt1ph5">
            <h1 className="mt-5 max-w-[15ch] font-display text-[40px] font-semibold leading-[1.04] tracking-[-0.035em] text-cream sm:text-[52px] lg:max-w-none lg:text-[58px]" data-id="element-139" data-mp="9pdtbkaczwdn">
              {hero.headline}
            </h1>
          </Reveal>

          <Reveal onMount delay={0.11} data-id="element-140" data-mp="3rtfiam4gcvph">
            <p className="mt-6 max-w-[54ch] text-[15.5px] leading-relaxed text-charcoal-200" data-id="element-141" data-mp="3ruh15crpmmhr">
              {hero.body}
            </p>
          </Reveal>

          <Reveal onMount delay={0.17} data-id="element-142" data-mp="nf7i62lv6gnd">
            <div className="mt-9 flex flex-wrap items-center gap-3" data-id="element-143" data-mp="ng8wcfy6fj9b">
              <a href="#demo" className="inline-flex items-center gap-2 rounded-lg bg-gold-400 px-5 py-3 text-[13.5px] font-semibold text-charcoal-800 transition-[background-color,transform] duration-150 ease-smooth hover:bg-gold-300 active:scale-[0.98]" data-id="element-144" data-mp="io8xyrxr8yeu">
                Book a demonstration
                <ArrowRightIcon size={15} strokeWidth={2.2} aria-hidden="true" data-id="element-145" data-mp="16cihtoberc3c" />
              </a>
              <a href="#story" className="inline-flex items-center rounded-lg border border-white/20 px-5 py-3 text-[13.5px] font-semibold text-cream transition-[background-color,border-color,transform] duration-150 ease-smooth hover:border-white/40 hover:bg-white/5 active:scale-[0.98]" data-id="element-146" data-mp="30n8kwjrrg0sk">
                See how KeyTurn works
              </a>
            </div>
          </Reveal>

          <Reveal onMount delay={0.22} data-id="element-147" data-mp="2hxiltkgyssuu">
            <ul className="mt-9 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-white/10 pt-6" data-id="element-148" data-mp="2musmxxdpusyi">
              {heroScope.map(item => <li key={item} className="whitespace-nowrap text-[12.5px] text-charcoal-300 before:mr-5 before:text-charcoal-500 before:content-['·'] first:before:hidden" data-id="element-149" data-mp="27zcpdidxgexd">
                  {item}
                </li>)}
            </ul>
          </Reveal>
        </div>

        <Reveal onMount delay={0.14} data-id="element-150" data-mp="1vmr0c4ipzjrp">
          <div className="rounded-2xl border border-white/10 bg-charcoal-800 p-5 sm:p-6" data-id="element-151" data-mp="1vnsc6b7k8aah">
            {healthLed && <div className="flex items-center gap-5 border-b border-white/10 pb-5" data-id="element-152" data-mp="1qluo93o7srb7">
                <HealthDial score={64} previous={69} size={92} onDark animate delay={0.24} data-id="element-153" data-mp="25eer037gcws4" />
                <div className="min-w-0" data-id="element-154" data-mp="20kiuz6ar1jzl">
                  <p className="micro-label text-[9px] text-charcoal-300" data-id="element-155" data-mp="2edi6yybbow2e">Portfolio Health</p>
                  <p className="mt-2 font-display text-[17px] font-medium leading-snug tracking-[-0.02em] text-cream" data-id="element-156" data-mp="2abx0pvtang16">
                    Two openings need intervention this week
                  </p>
                  <p className="mt-1.5 text-[11.5px] text-charcoal-400" data-id="element-157" data-mp="2jcldkn6ybach">
                    Down 5 since last week, across 14 live projects
                  </p>
                </div>
              </div>}

            <div className={`flex items-baseline justify-between gap-4 border-b border-white/10 pb-4 ${healthLed ? 'pt-5' : ''}`} data-id="element-158" data-mp="oz3nasf5vzdr">
              <p className="micro-label text-[9px] text-charcoal-300" data-id="element-159" data-mp="vekcykd1cafp">All openings</p>
              <p className="text-[11.5px] text-charcoal-400" data-id="element-160" data-mp="10dx8dthz2ptw">
                {healthLed ? 'Ranked by Health' : 'KeyTurn Health · this week'}
              </p>
            </div>

            <ul className="divide-y divide-white/[0.07]" data-id="element-161" data-mp="15axf86mnbwm2">
              {openings.map((opening, index) => {
              const needsAttention = opening.score < 50;
              return <li key={opening.name} className={`flex items-start gap-4 py-4 ${needsAttention ? '-mx-3 rounded-lg bg-white/[0.04] px-3' : ''}`} data-id="element-162" data-mp="com78awpmtf8" data-mpi={index}>
                    <HealthDial score={opening.score} previous={opening.previous} size={rowDialSize} onDark animate delay={(healthLed ? 0.44 : 0.3) + index * 0.09} data-id="element-163" data-mp="3l4xr06c4fjdp" />
                    <div className="min-w-0 flex-1" data-id="element-164" data-mp="237ucd8c1h1o4">
                      <p className="truncate font-display text-[15px] font-medium tracking-[-0.01em] text-cream" data-id="element-165" data-mp="1y7g1zjmytfmx">
                        {opening.name}
                      </p>
                      <p className="mt-0.5 text-[11.5px] text-charcoal-400" data-id="element-166" data-mp="1tafvj7m7eava">
                        {opening.phase} · {opening.runway}
                      </p>
                      <p className={`mt-1.5 text-[12.5px] leading-snug ${needsAttention ? 'text-clay-300' : 'text-charcoal-300'}`} data-id="element-167" data-mp="1jhh4zk75ca91">
                        {opening.note}
                      </p>
                    </div>
                  </li>;
            })}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>;
}